import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import { v4 as randomId } from "uuid";
import { unwrapResult } from "@reduxjs/toolkit";
import { toast } from "react-toastify";

import {
  CustomAlert,
  CustomButton,
  Picker,
} from "../../../../../shared/uiComponents";
import {
  dateNormalizer,
  errorNormalizer,
} from "../../../../../shared/Helpers/functions";
import AuthorizationDetail from "../EditInsuranceAuthorization/authorizationDetail";
import AddAuthorizationContent from "../EditInsuranceAuthorization/addAuthorizationContent";

import { DispatchProperties } from "../../../../../redux/store";
import {
  getInsuranceAuthorizations,
  getInsuranceAuthorizationSessionTypes,
  saveAssessmentInsuranceAuthorizations,
  saveTreatmentInsuranceAuthorizations,
} from "../../../../../redux/State/clientSlice/insuranceAuthorizationSlice";
import {
  ActiveAuthorizationProperties,
  AddAuthorizationDetailsProperties,
  AuthorizationProperties,
} from "../../../../../redux/API/ClientAPIHelpers/insuranceAuthorizationProperties";

export interface EditAuthorizationProperties
  extends AddAuthorizationDetailsProperties {
  id: string;
}
const todayDayjs = dayjs();
const futureDateWeek = dayjs().add(1, "week");

const CopyAuthorizationContent = ({
  authorizationData,
  onClose,
}: {
  authorizationData: ActiveAuthorizationProperties;
  onClose: () => void;
}) => {
  const dispatch = useDispatch<DispatchProperties>();
  const { clientId } = useParams();
  const [params] = useSearchParams();

  const [loading, setLoading] = useState<boolean>(false);
  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [startDateValue, setStartDateValue] = useState<Dayjs>(todayDayjs);
  const [endDateValue, setEndDateValue] = useState<Dayjs>(futureDateWeek);
  const [details, setDetails] = useState<Array<EditAuthorizationProperties>>(
    authorizationData.authorizationDetails.map((x) => ({
      id: randomId(),
      authorizationSessionTypeId: x.sessionType.id,
      frequency: x.frequency.id,
      totalHours: x.totalHours,
      hoursPerWeek: x.frequency.id === 1 ? x.hoursPerWeek : null,
    }))
  );

  const onChangeTreatmentStartDate = (value: Dayjs | null) => {
    if (!value) return;
    setStartDateValue(value);
  };

  const onChangeTreatmentEndDate = (value: Dayjs | null) => {
    if (!value) return;
    setEndDateValue(value);
  };

  const copyInsuranceAuthorization = () => {
    if (!clientId) return;
    const startDate = dateNormalizer(startDateValue);
    const endDate = dateNormalizer(endDateValue);

    const data: AuthorizationProperties = {
      authorizationDetails: details,
      endDate,
      startDate,
    };

    const copyAuthorization =
      authorizationData.type.id === 1
        ? saveAssessmentInsuranceAuthorizations
        : saveTreatmentInsuranceAuthorizations;
    setLoading(true);
    dispatch(copyAuthorization({ data, clientId }))
      .then(unwrapResult)
      .then((response) => {
        if (!response.isSuccessful) {
          throw new Error("Failed");
        }
        toast("Success");
        onClose();
        const page = params.get("page") || "1";
        const pageSize = params.get("pagaSize") || "8";
        dispatch(getInsuranceAuthorizations({ clientId, page, pageSize }));
      })
      .catch(errorNormalizer)
      .finally(() => setLoading(false));
  };

  const updateAuthorizationDetailsHandler = useCallback(
    (item: EditAuthorizationProperties) => {
      const updatedData = details.map((detail) => {
        if (detail.id !== item.id) {
          return detail;
        }
        return item;
      });
      setDetails(updatedData);
    },
    [details]
  );

  const addAuthorizationDetailsHandler = useCallback(
    (item: EditAuthorizationProperties) => {
      setDetails([...details, item]);
      setOpenAdd(false);
    },
    [details]
  );

  const removeAuthorizationDetailsHandler = useCallback(
    (item: EditAuthorizationProperties) => {
      const updatedData = details.filter((x) => x.id !== item.id);
      setDetails(updatedData);
    },
    [details]
  );

  useEffect(() => {
    dispatch(
      getInsuranceAuthorizationSessionTypes(`${authorizationData.type.id}`)
    );
  }, [dispatch, authorizationData]);

  return (
    <>
      <div
        style={{ display: "flex", gap: "16px", alignItems: "center" }}
        className="marginBottom16"
      >
        <Picker.CustomDate
          label="Start Date:"
          value={startDateValue}
          onChange={onChangeTreatmentStartDate}
        />
        <Picker.CustomDate
          label="End Date:"
          value={endDateValue}
          onChange={onChangeTreatmentEndDate}
        />
      </div>
      <div
        style={{ display: "flex", gap: "16px", alignItems: "center" }}
        className="marginBottom16"
      >
        <CustomButton
          title="Save"
          onClick={copyInsuranceAuthorization}
          loading={loading}
        />
        {authorizationData.type.id === 2 && (
          <CustomButton title="Add New" onClick={() => setOpenAdd(true)} />
        )}
      </div>
      {details.map((x) => (
        <AuthorizationDetail
          item={x}
          startDateValue={startDateValue}
          endDateValue={endDateValue}
          authorizationTypeId={authorizationData.type.id}
          update={updateAuthorizationDetailsHandler}
          remove={removeAuthorizationDetailsHandler}
        />
      ))}
      <CustomAlert
        open={openAdd}
        onClose={() => setOpenAdd(false)}
        Content={() => (
          <AddAuthorizationContent
            endDateValue={endDateValue}
            startDateValue={startDateValue}
            update={addAuthorizationDetailsHandler}
          />
        )}
      />
    </>
  );
};

export default CopyAuthorizationContent;
